import {isString} from 'lodash'
import moment, {Moment} from 'moment-timezone'

import {
  DateRange,
  ParticularShift,
  Shift,
  CalculationTimeFrameType,
  ExtendedShiftQuickSelectSlots,
  SHIFT_QUICK_SELECT_SLOTS,
  FilterTimeRange,
  Iso8601,
  SHIFT_QUICK_SELECT_SLOT_LIST,
  ShiftNotFoundError
} from '../types'

import {timezonedDateValidator, URL_DATE_FORMAT} from './datetime.utils'
import {
  getShift,
  getShiftsForDay,
  alignToShiftEnd,
  alignToShiftStart,
  getShiftBounds,
  getTimeRangeForShift,
  sortDateShiftsOnStartTime,
  getTimeRangeForShiftBeforeShift,
  getRequiredShift
} from './shift.utils'

export const QuickSelectSlotsToCalculationTimeFrameTypeMap: Record<
  ExtendedShiftQuickSelectSlots,
  CalculationTimeFrameType | undefined
> = {
  today: CalculationTimeFrameType.Today,
  yesterday: CalculationTimeFrameType.Yesterday,
  last3days: CalculationTimeFrameType.Last3Days,
  last7days: CalculationTimeFrameType.Last7Days,
  last30days: CalculationTimeFrameType.Last30Days,
  currentMonth: CalculationTimeFrameType.CurrentMonth,
  lastMonth: CalculationTimeFrameType.LastMonth,
  last24hours: CalculationTimeFrameType.Last24Hours,
  currentShift: CalculationTimeFrameType.CurrentShift,
  pastShift: CalculationTimeFrameType.PastShift,
  shiftBeforePastShift: CalculationTimeFrameType.ShiftBeforePastShift,
  theDayBeforeYesterday: CalculationTimeFrameType.TheDayBeforeYesterday,
  lastWeekend: CalculationTimeFrameType.LastWeekend,
  theWeekendBeforeLast: CalculationTimeFrameType.TheWeekendBeforeLast,
  monthBeforeLastMonth: CalculationTimeFrameType.MonthBeforeLastMonth,
  next30days: undefined,
  rolling12months: undefined
}

export const SHIFT_QUICK_SELECT_SLOTS_DEFAULT = SHIFT_QUICK_SELECT_SLOTS

function getCurrentShiftTiming(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const currentShift = getShift(now, shifts)
  const endDate = currentShift?.endDate
  return [currentShift?.startDate, endDate]
}

function getPastShiftTiming(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const currentShift = getShift(now, shifts)

  let pastShift: Shift | null = null

  if (!currentShift) {
    const {startDate, endDate} = getRequiredShift(now, shifts, true)
    return [startDate, endDate]
  }

  const currentShiftStartTime = currentShift.startDate.clone().add(1, 'second')

  const todayShifts: ParticularShift[] = sortDateShiftsOnStartTime(currentShiftStartTime, shifts)

  // Alternative: instead check if current time is between todayShifts[0] startDate and endDate
  if (
    todayShifts[0].startDate.isSame(currentShift.startDate) &&
    todayShifts[0].endDate.isSame(currentShift.endDate)
  ) {
    // desired past shift for the first shift of the day is the last shift of yesterday
    const yesterday = currentShiftStartTime.clone().subtract(1, 'day')
    const yesterdayShifts = sortDateShiftsOnStartTime(yesterday, shifts)
    if (yesterdayShifts.length === 0) {
      pastShift = getRequiredShift(yesterday, shifts, true)
    } else {
      pastShift = yesterdayShifts[yesterdayShifts.length - 1]
    }
  } else {
    for (let i = 1; i < todayShifts.length; i++) {
      const {startDate, endDate} = todayShifts[i]
      const startCheck = startDate.isSame(currentShift.startDate),
        endCheck = endDate.isSame(currentShift.endDate)
      if (startCheck && endCheck) {
        pastShift = todayShifts[i - 1]
        break
      }
    }
  }

  const {startDate, endDate} = getTimeRangeForShift(currentShiftStartTime, pastShift)
  return [startDate, endDate]
}

function getShiftBeforePastShiftTiming(now: Moment, shifts: Shift[]): [start: Moment, end: Moment] {
  const currentShift = getShift(now, shifts)
  if (!currentShift) throw new Error('unable to find current shift')

  const currentShiftStartTime = currentShift.startDate.clone().add(1, 'second')

  const todayShifts: ParticularShift[] = sortDateShiftsOnStartTime(currentShiftStartTime, shifts)
  let pastShift: Shift | null = null
  let isYesterdaysShift: boolean = false
  if (
    todayShifts[0].startDate.isSame(currentShift.startDate) &&
    todayShifts[0].endDate.isSame(currentShift.endDate)
  ) {
    // desired past shift for the first shift of the day is the shift before the last shift of yesterday
    const yesterday = currentShiftStartTime.clone().subtract(1, 'day')
    const yesterdayShifts = sortDateShiftsOnStartTime(yesterday, shifts)
    pastShift = yesterdayShifts[yesterdayShifts.length - 2]
    isYesterdaysShift = true
  } else if (
    todayShifts[1].startDate.isSame(currentShift.startDate) &&
    todayShifts[1].endDate.isSame(currentShift.endDate)
  ) {
    // desired past shift for the second shift of the day is the last shift of yesterday
    const yesterday = currentShiftStartTime.clone().subtract(1, 'day')
    const yesterdayShifts = sortDateShiftsOnStartTime(yesterday, shifts)
    pastShift = yesterdayShifts[yesterdayShifts.length - 1]
    isYesterdaysShift = true
  } else {
    for (let i = 2; i < todayShifts.length; i++) {
      const {startDate, endDate} = todayShifts[i]
      const startCheck = startDate.isSame(currentShift.startDate),
        endCheck = endDate.isSame(currentShift.endDate)
      if (startCheck && endCheck) {
        pastShift = todayShifts[i - 2]
        break
      }
    }
  }

  const {startDate, endDate} = getTimeRangeForShiftBeforeShift(
    currentShiftStartTime,
    pastShift!,
    isYesterdaysShift
  )
  return [startDate, endDate]
}

function getTodayTiming(now: Moment): [start: Moment, end: Moment] {
  const today = now.clone().startOf('day')
  return [today, now.startOf('minute')]
}

function getLast24hoursTiming(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const currentShift = getShift(now, shifts)
  const endDate = currentShift?.endDate
  return [endDate?.clone().subtract(24, 'hours'), endDate]
}

function getYesterdayShiftTiming(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const yesterday = now.clone().subtract(1, 'day')
  const yesterdaysShifts = getShiftsForDay(yesterday, shifts)
  if (yesterdaysShifts.length === 0) return [undefined, undefined]
  const sortedShifts = yesterdaysShifts.sort((a, b) => (a.start < b.start ? 0 : 1))
  const startShift: ParticularShift | undefined = sortedShifts[0]
  const start = startShift && alignToShiftStart(yesterday, startShift)
  const endShift: ParticularShift | undefined = sortedShifts[sortedShifts.length - 1]
  const end = endShift && alignToShiftEnd(yesterday, endShift)
  return [start, end]
}

function getTheDayBeforeYesterdayTiming(
  now: Moment,
  shifts: Shift[]
): [start?: Moment, end?: Moment] {
  const theDayBeforeYesterday = now.clone().subtract(2, 'day')
  return getYesterdayShiftTiming(theDayBeforeYesterday, shifts)
}

function getLastWeekendShiftTimings(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const sun: Moment = now.clone().subtract(now.day(), 'day')
  const fri: Moment = sun.clone().subtract(2, 'day')
  const sunShifts = getShiftsForDay(sun, shifts)
  const sortedSunShifts = sunShifts.sort((a, b) => (a.start < b.start ? 0 : 1))
  const friShifts = getShiftsForDay(fri, shifts)
  const sortedFriShifts = friShifts.sort((a, b) => (a.start < b.start ? 0 : 1))

  const fridayShift: ParticularShift | undefined = sortedFriShifts[sortedFriShifts.length - 1]
  const start = fridayShift && alignToShiftStart(fri, fridayShift)
  const endShift: ParticularShift | undefined = sortedSunShifts[sortedSunShifts.length - 1]
  const end = endShift && alignToShiftEnd(sun, sortedSunShifts[sortedSunShifts.length - 1])
  return [start, end]
}

function getTheWeekendBeforeLastTiming(
  now: Moment,
  shifts: Shift[]
): [start?: Moment, end?: Moment] {
  const sundayBeforeLastWeekend = now.clone().subtract(7, 'days')
  return getLastWeekendShiftTimings(sundayBeforeLastWeekend, shifts)
}

function getLast3days(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const last3rdDay: Moment = now.clone().subtract(3, 'day')
  return getShiftBounds(shifts, last3rdDay, now, true)
}

function getLast7days(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const last7thDay: Moment = now.clone().subtract(7, 'day')
  return getShiftBounds(shifts, last7thDay, now, true)
}

function getLast30days(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const last30thDay: Moment = now.clone().subtract(30, 'day')
  return getShiftBounds(shifts, last30thDay, now, true)
}

function getNext30days(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const next30days: Moment = now.clone().add(30, 'day')
  return getShiftBounds(shifts, now, next30days)
}

function getRolling12months(now: Moment, shifts: Shift[]): [start?: Moment, end?: Moment] {
  const last12Months = now.clone().subtract(12, 'month')
  return getShiftBounds(shifts, last12Months, now, true)
}

function getCurrentMonthTiming(now: Moment): [start: Moment, end: Moment] {
  const currentMonthStart = now.clone().startOf('month')
  const currentMonthEnd = now.clone().endOf('month')
  return [currentMonthStart, currentMonthEnd]
}

function getLastMonthTiming(now: Moment): [start: Moment, end: Moment] {
  const lastMonthStart = now.clone().subtract(1, 'month').startOf('month')
  const lastMonthEnd = now.clone().subtract(1, 'month').endOf('month')
  return [lastMonthStart, lastMonthEnd]
}

function getMonthBeforeLastMonthTiming(now: Moment): [start: Moment, end: Moment] {
  const monthBeforeLastMonthStart = now.clone().subtract(2, 'months').startOf('month')
  const monthBeforeLastMonthEnd = now.clone().subtract(2, 'months').endOf('month')
  return [monthBeforeLastMonthStart, monthBeforeLastMonthEnd]
}

const slotFunctionsMap = {
  currentShift: getCurrentShiftTiming,
  pastShift: getPastShiftTiming,
  today: getTodayTiming,
  last24hours: getLast24hoursTiming,
  yesterday: getYesterdayShiftTiming,
  lastWeekend: getLastWeekendShiftTimings,
  last3days: getLast3days,
  last7days: getLast7days,
  last30days: getLast30days,
  next30days: getNext30days,
  rolling12months: getRolling12months,
  lastMonth: (now: Moment) => getLastMonthTiming(now),
  currentMonth: (now: Moment) => getCurrentMonthTiming(now),
  shiftBeforePastShift: getShiftBeforePastShiftTiming,
  theDayBeforeYesterday: getTheDayBeforeYesterdayTiming,
  theWeekendBeforeLast: getTheWeekendBeforeLastTiming,
  monthBeforeLastMonth: (now: Moment) => getMonthBeforeLastMonthTiming(now)
}

export function getQuickSelectFilter(
  slot: ExtendedShiftQuickSelectSlots,
  shifts: Shift[],
  now: Moment
): [start?: Moment, end?: Moment] {
  if (!slotFunctionsMap[slot]) {
    throw new Error(`Unknown predefined time slot: ${slot}`)
  }

  return slotFunctionsMap[slot](now, shifts)
}

export const generateTimeRange = (
  filterTimeRange: DateRange | ExtendedShiftQuickSelectSlots | undefined,
  shifts: Shift[],
  plantNow: () => Moment
): FilterTimeRange => {
  let timeRange: {
    startDate: Iso8601 | null
    endDate: Iso8601 | null
  }

  if (typeof filterTimeRange === 'object') {
    timeRange = {
      startDate: filterTimeRange.startDate.toISOString(),
      endDate: filterTimeRange.endDate.toISOString()
    }
  } else if (typeof filterTimeRange === 'string') {
    const [startDate, endDate] = getQuickSelectFilter(filterTimeRange, shifts, plantNow())
    if (!startDate || !endDate) throw new ShiftNotFoundError()
    timeRange = {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    }
  } else {
    timeRange = {startDate: null, endDate: null}
  }

  return timeRange
}

export const generateTimeRangeFromUrlParam = (utcTimeRange: string, timezone: string) => {
  let result
  if (SHIFT_QUICK_SELECT_SLOT_LIST.includes(utcTimeRange as SHIFT_QUICK_SELECT_SLOTS)) {
    result = utcTimeRange
  } else {
    const [startDate, endDate] = utcTimeRange.split(',')
    result = {
      startDate: moment.utc(startDate, URL_DATE_FORMAT).tz(timezone),
      endDate: moment.utc(endDate, URL_DATE_FORMAT).tz(timezone)
    }
  }
  return result
}

export const getTimeRangeOrQuickSelectSlotsString = (
  timeRange: DateRange | ExtendedShiftQuickSelectSlots
): string => {
  if (isString(timeRange)) {
    return timeRange
  } else {
    const {startDate, endDate} = timeRange
    if (!startDate || !endDate)
      throw new Error('if either of startDate or endDate is given, both must be set')
    return getTimeRangeString(startDate.clone().utc(), endDate.clone().utc())
  }
}

export const getTimeRangeString = (startDateUtc: Moment, endDateUtc: Moment) => {
  timezonedDateValidator(startDateUtc, endDateUtc)
  return `${startDateUtc.format(URL_DATE_FORMAT)},${endDateUtc.format(URL_DATE_FORMAT)}`
}

export const generateQueryString = (queryObject: Record<string, string>) => {
  const queryParams = new URLSearchParams()
  for (const key in queryObject) {
    if (queryObject[key] !== undefined) {
      queryParams.append(key, queryObject[key])
    }
  }
  return queryParams.toString()
}
